const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

export const isBlankWorkspaceValue = (value) => value === undefined
  || value === null
  || (typeof value === 'string' && value.trim() === '')
  || (Array.isArray(value) && value.length === 0)
  || (isRecord(value) && Object.keys(value).length === 0);

export const humanizeWorkspaceName = (name = '') => name
  .replace(/\[(\d+)\]/g, ' $1')
  .replace(/[._-]+/g, ' ')
  .replace(/([a-z\d])([A-Z])/g, '$1 $2')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatDate = (value) => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date);
};

export const sortGeneratedDocuments = (documents = []) => [...documents].sort((left, right) =>
  new Date(right.created_at || 0).getTime() - new Date(left.created_at || 0).getTime()
);

export const formatWorkspaceValue = (value) => {
  if (isBlankWorkspaceValue(value)) return 'No value supplied';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return formatDate(value);
  if (typeof value === 'number') return String(value);
  return Array.isArray(value) ? `${value.length} item${value.length === 1 ? '' : 's'}` : 'Structured value';
};

export const searchableWorkspaceValue = (value) => {
  if (isBlankWorkspaceValue(value)) return 'No value supplied';
  if (Array.isArray(value)) return value.map(searchableWorkspaceValue).join(' ');
  if (isRecord(value)) return Object.entries(value)
    .map(([key, child]) => `${humanizeWorkspaceName(key)} ${searchableWorkspaceValue(child)}`)
    .join(' ');
  return formatWorkspaceValue(value);
};

export const getWorkspaceValueAtPath = (values, path) => path
  .replace(/\[(\d+)\]/g, '.$1')
  .split('.')
  .filter(Boolean)
  .reduce((value, part) => value?.[part], values);

const flattenValues = (values = {}) => {
  const flattened = [];
  const blanks = [];
  const collections = [];
  const walk = (value, name) => {
    if (Array.isArray(value)) {
      collections.push({ name, item_count: value.length });
      if (value.length === 0) blanks.push(name);
      value.forEach((item, index) => walk(item, `${name}[${index}]`));
      return;
    }
    if (isRecord(value)) {
      const entries = Object.entries(value);
      if (entries.length === 0) {
        flattened.push({ name, value });
        blanks.push(name);
        return;
      }
      entries.forEach(([key, child]) => walk(child, name ? `${name}.${key}` : key));
      return;
    }
    flattened.push({ name, value });
    if (isBlankWorkspaceValue(value)) blanks.push(name);
  };
  Object.entries(values).forEach(([key, value]) => walk(value, key));
  return { flattened_variables: flattened, blank_variables: blanks, collections };
};

export const getDocumentDiagnostics = (document) => {
  const values = document?.variables_json || {};
  if (values.merge_diagnostics) return values.merge_diagnostics;

  const { merge_diagnostics, ...mergeValues } = values;
  return {
    ...flattenValues(mergeValues),
    generated_at: document?.completed_at || document?.created_at || null,
    template_id: document?.document_template_id || null,
    template_version: document?.template?.version ?? null,
    is_legacy_fallback: true
  };
};

export const filterDiagnosticValues = (diagnostics, search = '', blanksOnly = false) => {
  const normalizedSearch = search.trim().toLowerCase();
  const blankNames = new Set(diagnostics?.blank_variables || []);
  return (diagnostics?.flattened_variables || []).filter((item) => {
    const isBlank = blankNames.has(item.name) || isBlankWorkspaceValue(item.value);
    if (blanksOnly && !isBlank) return false;
    if (!normalizedSearch) return true;
    return `${item.name} ${humanizeWorkspaceName(item.name)} ${searchableWorkspaceValue(item.value)}`
      .toLowerCase()
      .includes(normalizedSearch);
  });
};

export const filterDiagnosticCollections = (diagnostics, variables, search = '', blanksOnly = false) => {
  const normalizedSearch = search.trim().toLowerCase();
  return (diagnostics?.collections || []).filter((collection) => {
    const value = getWorkspaceValueAtPath(variables, collection.name);
    const isBlank = isBlankWorkspaceValue(value);
    if (blanksOnly && !isBlank) return false;
    if (!normalizedSearch) return true;
    return `${collection.name} ${humanizeWorkspaceName(collection.name)} ${searchableWorkspaceValue(value)}`
      .toLowerCase()
      .includes(normalizedSearch);
  });
};

export const selectGeneratedDraft = (documents, selectedId) => {
  const sorted = sortGeneratedDocuments(documents);
  return sorted.find((document) => document.id === selectedId)
    || sorted.find((document) => document.status === 'completed')
    || sorted[0]
    || null;
};

export const addGeneratedDraft = (documents, generatedDocument) => {
  const withoutDuplicate = documents.filter((document) => document.id !== generatedDocument.id);
  const drafts = sortGeneratedDocuments([generatedDocument, ...withoutDuplicate]);
  return { drafts, selectedId: generatedDocument.id };
};

export const previewMessage = (error) => error?.message || error || 'Unable to preview this DOCX draft.';
